/**
 * ReservationForm.jsx — 에어카텔 예약 신규 등록 (Phase 3-B, 2026-04-30)
 *
 * URL: /admin/reservations/new
 * 권한: AIRTEL_RESERVATION_MANAGEMENT
 *
 * 단일 폼:
 *   1. 마스터 (travel_code 자동 채움 + 편집 가능)
 *   2. 항공 N (동적 추가/삭제)
 *   3. 호텔 N
 *   4. 렌트 N
 *   5. 결제 N
 *
 * 저장 시: POST /admin/reservations → 마스터/자식/결제 트랜잭션 저장 → 상세 화면 navigate.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/apiClient';
import ExpandableTextarea from '../../components/ExpandableTextarea';
import { todayKST } from '../../utils/date';
import HotelNightlyRatesEditor, { cleanHotelRows, hotelTotal } from './reservationmgmt/HotelNightlyRatesEditor';
import ReservationFlightRows from './reservationmgmt/ReservationFlightRows';
import ReservationPaymentRows from './reservationmgmt/ReservationPaymentRows';
import './reservationmgmt/ReservationFormLayout.css';
import { PAYMENT_METHOD_OPTS, PAX_OPTS, STATUS_OPTS } from './reservationFormConfig';
import ReservationFormSummary from './ReservationFormSummary';
import { validateReservationForm } from './reservationFormValidation';
import { useReservationFieldSettings } from './useReservationFieldSettings';
// 줄 도우미(항공·호텔·렌트·결제)는 reservationFormRows.js 로 옮김(2026-10-27, 300줄 규칙)
import {
  blank, blankHotel, MASTER_OPTIONAL_KEYS, MASTER_EXTRA_KEYS, flightTotal, cleanRows, hasUserInput,
  cleanFlights, normalizePaymentRow, cleanPayments, blankPayment,
} from './reservationFormRows';

const inputStyle = { padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg)', color: 'var(--text)', width: '100%', maxWidth: '100%', minWidth: 0, height: 30, boxSizing: 'border-box', fontSize: 12 };
const readOnlyInputStyle = { ...inputStyle, background: 'rgba(148,163,184,0.08)', fontWeight: 800, textAlign: 'right' };
const labelStyle = { fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 };

function numberValue(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  const raw = Number.isFinite(n) ? String(Math.trunc(n)) : String(value).split('.')[0];
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function parseNumber(value) {
  const integerPart = String(value || '').replace(/,/g, '').split('.')[0];
  const digits = integerPart.replace(/[^\d]/g, '');
  return digits === '' ? null : Number(digits);
}

function Field({ label, value, onChange, type = 'text', required, textarea, options, readOnly, defaultTodayOnFocus }) {
  const isFormattedNumber = type === 'amount' || type === 'integer' || type === 'number';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
      <span style={labelStyle}>{label}{required && <span style={{ color: '#dc2626' }}> *</span>}</span>
      {options ? (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} style={inputStyle}>
          <option value="">선택</option>
          {options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
        </select>
      ) : textarea ? (
        <ExpandableTextarea
          value={value}
          onChange={onChange}
          rows={2}
          expandedRows={6}
          style={{ ...inputStyle, height: 'auto', minHeight: 52, resize: 'vertical' }}
        />
      ) : (
        <input
          type={isFormattedNumber ? 'text' : type}
          inputMode={isFormattedNumber ? 'numeric' : undefined}
          pattern={isFormattedNumber ? '[0-9,]*' : undefined}
          value={isFormattedNumber ? numberValue(value) : (value ?? '')}
          readOnly={readOnly}
          onFocus={(e) => {
            if (isFormattedNumber) e.target.select();
            if (defaultTodayOnFocus && type === 'date' && !value) onChange(todayKST());
          }}
          onChange={(e) => {
            if (readOnly) return;
            const v = e.target.value;
            if (isFormattedNumber) onChange(parseNumber(v));
            else onChange(v || null);
          }}
          style={readOnly ? readOnlyInputStyle : inputStyle} />
      )}
    </div>
  );
}

const Section = ({ title, action, children }) => (
  <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, padding: 6, display: 'grid', gap: 6, minWidth: 0, boxSizing: 'border-box' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{title}</span>
      {action}
    </div>
    {children}
  </div>
);

const Grid = ({ children, cols = 4 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(min(${cols === 4 ? 160 : 190}px, 100%), 1fr))`, gap: 6, minWidth: 0 }}>{children}</div>
);

export default function ReservationForm() {
  const navigate = useNavigate();
  // 회사는 보내지 않는다 — 서버가 장부로 정한다(운영자=사이트 주인 회사 / 테넌트=자기 회사). 예전 'PINE' 기본값은 미팅링크 예약을 PINE 으로 보냈다(2026-10-27).
  const [master, setMaster] = useState({ status: 'confirmed', reservation_date: todayKST(), adult_count: 0, child_count: 0, infant_count: 0 });
  const [flights, setFlights] = useState(() => Array.from({ length: 4 }, () => blank()));
  const [hotels, setHotels] = useState([blankHotel()]);
  const [cars, setCars] = useState([blank()]);
  const [payments, setPayments] = useState([blankPayment()]);
  const [saving, setSaving] = useState(false);
  const [showHiddenFields, setShowHiddenFields] = useState(false);
  const { hiddenSet } = useReservationFieldSettings();
  const hiddenFields = hiddenSet;
  const showField = (key) => showHiddenFields || !hiddenFields.has(key);
  const showMasterOptional = MASTER_OPTIONAL_KEYS.some(showField);
  const showMasterExtra = MASTER_EXTRA_KEYS.some(showField);

  // travel_code 자동 채우기 (마운트 시 1회)
  useEffect(() => {
    apiClient.get('/api/admin/reservations/next-code').then((res) => {
      if (res.ok && res.data?.success) setMaster((m) => ({ ...m, travel_code: res.data.data.travel_code }));
    }).catch(() => {});
  }, []);

  const updMaster = (k) => (v) => setMaster((m) => ({ ...m, [k]: v }));
  const updChild  = (setter, idx) => (k) => (v) => setter((arr) => arr.map((r, i) => i === idx ? { ...r, [k]: v } : r));
  const updFlight = (idx) => (k) => (v) => setFlights((arr) => arr.map((r, i) => {
    if (i !== idx) return r;
    const next = { ...r, [k]: v };
    if (['base_fare', 'fuel_surcharge', 'taxes'].includes(k)) next.total_payment = flightTotal(next);
    return next;
  }));
  const updateFlightCell = (idx, key, value) => updFlight(idx)(key)(value);
  const updatePaymentCell = (idx, key, value) => setPayments((arr) => arr.map((row, rowIndex) => {
    if (rowIndex !== idx) return row;
    const next = { ...row, [key]: value };
    return normalizePaymentRow(next);
  }));
  const updHotelRates = (idx) => (rates) => setHotels((arr) => arr.map((row, rowIndex) => (
    rowIndex === idx ? { ...row, nightly_rates: rates, total_cost: hotelTotal(rates) || null } : row
  )));
  const removeChild = (setter, idx) => () => setter((arr) => arr.filter((_, i) => i !== idx));
  const addChild = (setter) => () => setter((arr) => [...arr, blank()]);

  async function handleSave() {
    const validationMessage = validateReservationForm({ master, flights, cars, hotels, payments, isVisible: showField });
    if (validationMessage) { alert(validationMessage); return; }
    setSaving(true);
    try {
      const r = await apiClient.post('/api/admin/reservations', {
        master,
        flights: cleanFlights(flights),
        hotels: cleanHotelRows(hotels),
        cars: cleanRows(cars).filter(hasUserInput),
        payments: cleanPayments(payments),
      });
      if (!r.ok || !r.data?.success) { alert(r.data?.message || '저장 실패'); setSaving(false); return; }
      const code = r.data.data.travel_code;
      navigate(`/admin/reservations/${encodeURIComponent(code)}`);
    } catch (e) {
      alert('저장 오류: ' + e.message);
    } finally { setSaving(false); }
  }

  return (
    <div className="reservation-form-page">
      <div className="reservation-form-toolbar">
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>예약 등록</h2>
        <div className="reservation-form-actions">
          <button
            onClick={() => setShowHiddenFields((v) => !v)}
            style={showHiddenFields ? toggleActiveBtn : toggleBtn}>
            {showHiddenFields ? '간편 입력' : '전체 입력'}
          </button>
          <button onClick={() => navigate('/admin/reservations')} style={{ padding: '6px 14px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' }}>취소</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '6px 14px', background: 'var(--accent-700, #7c3aed)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>{saving ? '저장 중...' : '저장'}</button>
        </div>
      </div>

      <ReservationFormSummary flights={flights} cars={cars} hotels={hotels} payments={payments} />

      {/* 마스터 */}
      <Section title="예약 마스터">
        <div className="reservation-master-primary-grid">
          <Field label="여행코드" required value={master.travel_code} onChange={updMaster('travel_code')} />
          <Field label="고객명" required value={master.customer_name} onChange={updMaster('customer_name')} />
          <Field label="예약일" required type="date" value={master.reservation_date} onChange={updMaster('reservation_date')} />
          <Field label="메모" textarea value={master.notes} onChange={updMaster('notes')} />
        </div>
        {showMasterOptional && (
        <Grid cols={4}>
          {showField('customer_phone') && <Field label="연락처" value={master.customer_phone} onChange={updMaster('customer_phone')} />}
          {showField('travel_start') && <Field label="출발일" type="date" defaultTodayOnFocus value={master.travel_start} onChange={updMaster('travel_start')} />}
          {showField('travel_end') && <Field label="귀국일" type="date" defaultTodayOnFocus value={master.travel_end} onChange={updMaster('travel_end')} />}
          {showField('adult_count') && <Field label="성인" type="integer" value={master.adult_count} onChange={updMaster('adult_count')} />}
          {showField('child_count') && <Field label="소아" type="integer" value={master.child_count} onChange={updMaster('child_count')} />}
          {showField('infant_count') && <Field label="유아" type="integer" value={master.infant_count} onChange={updMaster('infant_count')} />}
        </Grid>
        )}
        {showMasterExtra && (
          <div style={hiddenBox}>
          <Grid cols={4}>
            {showField('status') && <Field label="상태" value={master.status} onChange={updMaster('status')} options={STATUS_OPTS} />}
            {showField('customer_email') && <Field label="이메일" value={master.customer_email} onChange={updMaster('customer_email')} />}
            {showField('source') && <Field label="예약 경로" value={master.source} onChange={updMaster('source')} />}
          </Grid>
          </div>
        )}
      </Section>

      <div className="reservation-product-layout">
        <Section title={`항공 (${flights.length})`} action={<button onClick={addChild(setFlights)} style={addBtn}>+ 추가</button>}>
          <ReservationFlightRows
            rows={flights}
            showField={showField}
            paxOptions={PAX_OPTS}
            flightTotal={flightTotal}
            onChange={updateFlightCell}
            onRemove={(index) => removeChild(setFlights, index)()}
          />
        </Section>

        <div className="reservation-side-stack">
          <Section title={`렌트 (${cars.length})`} action={<button onClick={addChild(setCars)} style={addBtn}>+ 추가</button>}>
            {cars.length === 0 && <Empty>렌트 예약 없음 — "+ 추가" 클릭</Empty>}
            {cars.map((c, i) => (
              <div key={i} style={rowBox}>
                <Grid cols={4}>
                  {showField('car.total_cost') && <Field label="총액" required type="amount" value={c.total_cost} onChange={updChild(setCars, i)('total_cost')} />}
                  {showField('car.rental_company') && <Field label="업체" value={c.rental_company} onChange={updChild(setCars, i)('rental_company')} />}
                  {showField('car.car_model') && <Field label="차종" value={c.car_model} onChange={updChild(setCars, i)('car_model')} />}
                  {showField('car.pickup_location') && <Field label="인수지" value={c.pickup_location} onChange={updChild(setCars, i)('pickup_location')} />}
                  {showField('car.return_location') && <Field label="반납지" value={c.return_location} onChange={updChild(setCars, i)('return_location')} />}
                  {showField('car.pickup_at') && <Field label="인수일시" value={c.pickup_at} onChange={updChild(setCars, i)('pickup_at')} />}
                  {showField('car.return_at') && <Field label="반납일시" value={c.return_at} onChange={updChild(setCars, i)('return_at')} />}
                  {showField('car.driver_name') && <Field label="운전자" value={c.driver_name} onChange={updChild(setCars, i)('driver_name')} />}
                  {showField('car.insurance_type') && <Field label="보험" value={c.insurance_type} onChange={updChild(setCars, i)('insurance_type')} />}
                </Grid>
                <button onClick={removeChild(setCars, i)} style={delBtn}>삭제</button>
              </div>
            ))}
          </Section>

          <Section title={`호텔 (${hotels.length})`} action={<button onClick={() => setHotels((arr) => [...arr, blankHotel()])} style={addBtn}>+ 추가</button>}>
            {hotels.length === 0 && <Empty>호텔 예약 없음 — "+ 추가" 클릭</Empty>}
            {hotels.map((h, i) => (
              <div key={i} style={rowBox}>
                <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
                  <Grid cols={4}>
                    {showField('hotel.hotel_name') && <Field label="호텔명" value={h.hotel_name} onChange={updChild(setHotels, i)('hotel_name')} />}
                    {showField('hotel.room_type') && <Field label="객실 타입" value={h.room_type} onChange={updChild(setHotels, i)('room_type')} />}
                    {showField('hotel.check_in') && <Field label="체크인" type="date" defaultTodayOnFocus value={h.check_in} onChange={updChild(setHotels, i)('check_in')} />}
                    {showField('hotel.check_out') && <Field label="체크아웃" type="date" defaultTodayOnFocus value={h.check_out} onChange={updChild(setHotels, i)('check_out')} />}
                    {showField('hotel.hotel_address') && <Field label="주소" value={h.hotel_address} onChange={updChild(setHotels, i)('hotel_address')} />}
                    {showField('hotel.room_count') && <Field label="객실 수" type="integer" value={h.room_count} onChange={updChild(setHotels, i)('room_count')} />}
                    {showField('hotel.nights') && <Field label="박" type="integer" value={h.nights} onChange={updChild(setHotels, i)('nights')} />}
                    {showField('hotel.guest_count') && <Field label="투숙객" type="integer" value={h.guest_count} onChange={updChild(setHotels, i)('guest_count')} />}
                    {showField('hotel.confirmation_no') && <Field label="확정번호" value={h.confirmation_no} onChange={updChild(setHotels, i)('confirmation_no')} />}
                  </Grid>
                  {(showField('hotel.total_cost') || showField('hotel.nightly_rates')) && (
                    <HotelNightlyRatesEditor rates={h.nightly_rates} onChange={updHotelRates(i)} compact />
                  )}
                </div>
                <button onClick={removeChild(setHotels, i)} style={delBtn}>삭제</button>
              </div>
            ))}
          </Section>
        </div>
      </div>

          <Section title={`결제 (${payments.length})`} action={<button onClick={() => setPayments((arr) => [...arr, blankPayment()])} style={addBtn}>+ 추가</button>}>
        <ReservationPaymentRows
          rows={payments}
          showField={showField}
          methodOptions={PAYMENT_METHOD_OPTS}
          onChange={updatePaymentCell}
          onRemove={(index) => removeChild(setPayments, index)()}
        />
      </Section>

    </div>
  );
}

const Empty = ({ children }) => <div style={{ padding: 8, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{children}</div>;
const rowBox = { padding: 7, border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 7, alignItems: 'end', minWidth: 0, boxSizing: 'border-box' };
const hiddenBox = { borderTop: '1px solid var(--border)', paddingTop: 6, display: 'grid', gap: 6 };
const toggleBtn = { padding: '6px 12px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 };
const toggleActiveBtn = { ...toggleBtn, borderColor: 'var(--accent-700, #7c3aed)', color: 'var(--accent-700, #7c3aed)', background: 'rgba(124,58,237,0.08)' };
const addBtn = { padding: '4px 10px', background: 'var(--accent-700, #7c3aed)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const delBtn = { padding: '2px 6px', minWidth: 36, height: 26, background: 'transparent', color: '#dc2626', border: '1px solid #dc2626', borderRadius: 4, cursor: 'pointer', fontSize: 10, alignSelf: 'end' };
